// src/components/fix-actions/fix-actions-page.tsx
import { useMemo, useEffect, useState, useCallback } from "react";
import { AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { FixActionsLoading } from "./fix-actions-loading";
import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { ModuleTabNavigation } from "../module-tab-navigation";
import { TabPanel } from "@/app/components/shared/tab-panel";
import { FixActionsHeader } from "./fix-actions-header";
import { QuickWinBriefing } from "./quick-win-briefing";
import { SuggestedActionsSmartInbox } from "./suggested-actions-smart-inbox";
import { OptimizationStudioTab } from "./optimization-studio-tab";
import { ActionLogTab } from "./action-log-tab";
import { TemplateGalleryModal } from "./template-gallery-modal";
import { ArtifactDetailTray } from "./artifact-detail-tray";
import {
  AssetGenerationTray,
  type TrayMode,
  type GenerationConfig,
} from "./asset-generation-tray";
import { useFixActionsData } from "../../hooks/fix-actions/useFixActionsData";
import {
  fixActionsService,
  artifactTracker,
  type DeployStatus,
} from "../../services/fix-actions/fix-actions-service";
import type { FixAction, ContentTemplate } from "../../services/fix-actions/fix-actions-service";
import {
  fetchOptimizationStudioData,
  type StrategyArtifact,
} from "../../services/fix-actions/optimization-studio-service";
import { ACTION_ITEMS_UI } from "./redesign/action-items-flag";
import { ActionItemsShell } from "./redesign/action-items-shell";
import { RedesignAssetGenerationTray } from "./redesign/asset-generation-tray";
import { RedesignArtifactDetailTray } from "./redesign/artifact-detail-tray";
import { ActionLogView } from "./redesign/action-log-view";

interface FixActionsPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  onNavigate?: (page: string) => void;
  brandName: string;
  brandId: string;
  selectedModels: string[];
  antennaData: any | null;
  cognitionData: any | null;
  initialHighlightActionTitle?: string | null;
  onHighlightConsumed?: () => void;
}

interface GenerationContext {
  actionTitle: string;
  actionTheme: string;
  artifactTitle: string;
  artifactType: string;
  artifactDescription: string;
  artifactIndex: number;
  artifactId?: string;
  actionId?: number;
  totalArtifacts?: number;
}

function getGenerationCtx(): GenerationContext {
  return (window as any).__generationContext || {};
}
function setGenerationCtx(ctx: Partial<GenerationContext>) {
  (window as any).__generationContext = { ...getGenerationCtx(), ...ctx };
}
function clearGenerationCtx() {
  delete (window as any).__generationContext;
}

async function pushEventStatus(
  brandId: string,
  suggestedActionId: number,
  totalArtifacts: number,
  generatedCount: number,
  closedCount: number,
  ingestedActionId?: number
) {
  const ids = new Set([suggestedActionId]);
  if (ingestedActionId && ingestedActionId !== suggestedActionId) {
    ids.add(ingestedActionId);
  }
  try {
    await Promise.all(
      Array.from(ids).map((actionId) =>
        fixActionsService.updateEventStatus({
          brandId,
          actionId,
          totalArtifacts,
          generatedCount,
          closedCount,
        })
      )
    );
    window.dispatchEvent(new Event("event_status_updated"));
  } catch {
    /* non-fatal */
  }
}

export default function FixActionsPage({
  isSidebarCollapsed = false,
  onSidebarToggle,
  onNavigate,
  brandName,
  brandId,
  selectedModels,
  antennaData,
  cognitionData,
  initialHighlightActionTitle,
  onHighlightConsumed,
}: FixActionsPageProps) {
  const persisted = (() => {
    try {
      return JSON.parse(localStorage.getItem("bm_last_result") || "{}");
    } catch {
      return {};
    }
  })();
  const effectiveBrandId = String(
    brandId || persisted?.runId || localStorage.getItem("bm_brand_id") || ""
  );

  const { data: fixActionsData, loading, error } = useFixActionsData({
    brandName,
    selectedModels,
    antennaData,
    cognitionData,
    brandId: effectiveBrandId,
  });

  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("actions");
  const [isFinishing, setIsFinishing] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const [isBriefingMinimized, setIsBriefingMinimized] = useState(false);
  const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [aiAssistantTopic, setAiAssistantTopic] = useState<string | undefined>(undefined);

  const [showGenerationTray, setShowGenerationTray] = useState(false);
  const [generationTrayMode, setGenerationTrayMode] = useState<TrayMode>("setup");
  const [generatingActionId, setGeneratingActionId] = useState<number | null>(null);
  const [generatingArtifact, setGeneratingArtifact] = useState<StrategyArtifact | null>(null);
  const [returnToTab, setReturnToTab] = useState<string>("actions");

  const [openArtifact, setOpenArtifact] = useState<StrategyArtifact | null>(null);
  const [optimisticStudioArtifacts, setOptimisticStudioArtifacts] = useState<
    StrategyArtifact[]
  >([]);
  const [completedActionIds, setCompletedActionIds] = useState<Set<number>>(new Set());
  const [highlightActionTitle, setHighlightActionTitle] = useState<string | null>(null);
  const [highlightStudioArtifactId, setHighlightStudioArtifactId] = useState<string | null>(
    null
  );
  const [trackerVersion, setTrackerVersion] = useState(0);

  useEffect(() => {
    if (!initialHighlightActionTitle) return;
    if (!hasFinished) return;

    setActiveTab("actions");
    setIsBriefingMinimized(true);
    setHighlightActionTitle(initialHighlightActionTitle);

    onHighlightConsumed?.();
  }, [initialHighlightActionTitle, hasFinished, onHighlightConsumed]);

  const safeFixActions: FixAction[] = useMemo(() => {
    const fa = (fixActionsData as any)?.fixActions;
    return Array.isArray(fa) ? fa : [];
  }, [fixActionsData]);

  const safeActionLog: FixAction[] = useMemo(() => {
    const al = (fixActionsData as any)?.actionLog;
    return Array.isArray(al) ? al : [];
  }, [fixActionsData]);

  const safeAntennaIngested: FixAction[] = useMemo(() => {
    const ai = (fixActionsData as any)?.antennaIngested;
    return Array.isArray(ai) ? ai : [];
  }, [fixActionsData]);

  const safeCognitionIngested: FixAction[] = useMemo(() => {
    const ci = (fixActionsData as any)?.cognitionIngested;
    return Array.isArray(ci) ? ci : [];
  }, [fixActionsData]);

  const mergedSuggestedActions: FixAction[] = useMemo(() => {
    const tagSource = (rows: FixAction[], fallback?: string): FixAction[] =>
      rows.map((r) => ({
        ...r,
        sourceModule: r.sourceModule || fallback,
      }));

    return [
      ...tagSource(safeFixActions, "suggested"),
      ...tagSource(safeAntennaIngested, "antenna"),
      ...tagSource(safeCognitionIngested, "cognition"),
    ];
  }, [safeFixActions, safeAntennaIngested, safeCognitionIngested]);

  const allActionsForLog: FixAction[] = useMemo(() => {
    if (safeActionLog.length === 0) return [];

    const suggestedBySourceId = new Map<string, FixAction>();
    const suggestedByTitle = new Map<string, FixAction>();
    for (const s of safeFixActions) {
      if (s.sourceId) suggestedBySourceId.set(String(s.sourceId), s);
      if (s.title) suggestedByTitle.set(s.title.trim().toLowerCase(), s);
    }

    return safeActionLog.map((logEntry) => {
      const sourceId = (logEntry as any).sourceId;
      const matched =
        (sourceId && suggestedBySourceId.get(String(sourceId))) ||
        (logEntry.title && suggestedByTitle.get(logEntry.title.trim().toLowerCase()));

      if (!matched) return logEntry;

      return {
        ...logEntry,
        artifacts: matched.artifacts?.length ? matched.artifacts : logEntry.artifacts,
        strategicTheme: logEntry.strategicTheme || matched.strategicTheme,
        observation: logEntry.observation || matched.observation,
        strategicGoal: logEntry.strategicGoal || matched.strategicGoal,
        reasoningInfluence: logEntry.reasoningInfluence || matched.reasoningInfluence,
        expectedOutcome: logEntry.expectedOutcome || matched.expectedOutcome,
        impactScore: logEntry.impactScore ?? matched.impactScore,
        impactLevel: logEntry.impactLevel || matched.impactLevel,
        effort: logEntry.effort || matched.effort,
        priority: logEntry.priority || matched.priority,
        category: logEntry.category || matched.category,
      } as FixAction;
    });
  }, [safeActionLog, safeFixActions]);

  const safeTemplates: ContentTemplate[] = useMemo(() => {
    const ct = (fixActionsData as any)?.contentTemplates;
    return Array.isArray(ct) ? ct : [];
  }, [fixActionsData]);

  const enrichedSuggestedActions: FixAction[] = useMemo(() => {
    return mergedSuggestedActions.map((a) =>
      completedActionIds.has(a.id) ? { ...a, status: "completed" } : a
    );
  }, [mergedSuggestedActions, completedActionIds]);

  const highPriorityCount = useMemo(
    () =>
      enrichedSuggestedActions.filter(
        (a) => a.priority === "HIGH" && a.status !== "completed"
      ).length,
    [enrichedSuggestedActions]
  );
  const quickWinsCount = useMemo(
    () =>
      enrichedSuggestedActions.filter(
        (a) =>
          a.effort === "Low" &&
          (a.impactLevel === "Medium" || a.impactLevel === "High") &&
          a.status !== "completed"
      ).length,
    [enrichedSuggestedActions]
  );
  const completedCount = useMemo(
    () => enrichedSuggestedActions.filter((a) => a.status === "completed").length,
    [enrichedSuggestedActions]
  );
  const quickWinAction: FixAction | null = useMemo(() => {
    const candidates = enrichedSuggestedActions
      .filter((a) => a.effort === "Low" && a.status !== "completed")
      .sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0));
    return candidates[0] || null;
  }, [enrichedSuggestedActions]);

  const filteredActions = useMemo(() => {
    return enrichedSuggestedActions
      .filter((action) => {
        if (filterCategory === "all") return true;
        if (filterCategory === "high-impact") return action.impactLevel === "High";
        if (filterCategory === "quick-wins")
          return (
            action.effort === "Low" &&
            (action.impactLevel === "Medium" || action.impactLevel === "High")
          );
        if (filterCategory === "foundational") return action.effort === "High";
        return true;
      })
      .filter(
        (action) =>
          searchQuery === "" ||
          action.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [enrichedSuggestedActions, filterCategory, searchQuery]);

  useEffect(() => {
    if (!loading && fixActionsData && !hasFinished) {
      setIsFinishing(true);
      const timer = setTimeout(() => {
        setHasFinished(true);
        setIsFinishing(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, fixActionsData, hasFinished]);

  useEffect(() => {
    if (!hasFinished) return;

    async function hydrateTracker() {
      try {
        const { artifacts } = await fetchOptimizationStudioData({
          brandId: effectiveBrandId,
        });

        const allActionsForHydration = [
          ...mergedSuggestedActions,
          ...safeActionLog,
        ];
        artifactTracker.hydrateFromArtifacts(artifacts, allActionsForHydration);

        if (artifacts.length > 0) {
          setOptimisticStudioArtifacts((prev) => {
            const optimisticById = new Map(prev.map((a) => [a.id, a]));
            const merged = artifacts.map((a) =>
              optimisticById.has(a.id) ? optimisticById.get(a.id)! : a
            );
            for (const opt of prev) {
              if (!artifacts.some((b) => b.id === opt.id)) {
                merged.unshift(opt);
              }
            }
            return merged;
          });
        }

        const newCompleted = new Set<number>();
        for (const action of mergedSuggestedActions) {
          const total = action.artifacts?.length ?? 0;
          if (total === 0) continue;
          if (artifactTracker.countClosed(action.id) >= total) {
            newCompleted.add(action.id);
          }
        }
        if (newCompleted.size > 0) {
          setCompletedActionIds((prev) =>
            new Set([...Array.from(prev), ...Array.from(newCompleted)])
          );
        }

        setTrackerVersion((v) => v + 1);
      } catch {
        // Non-fatal
      }
    }

    hydrateTracker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFinished, effectiveBrandId]);

  useEffect(() => {
    if (activeTab !== "actions") setHighlightActionTitle(null);
    if (activeTab !== "content") setHighlightStudioArtifactId(null);
  }, [activeTab]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        artifactId: string;
        actionId: number;
        actionTitle?: string;
      };
      if (!detail?.actionId) return;

      const actionId = detail.actionId;

      if (detail.artifactId) {
        artifactTracker.updateStatus(detail.artifactId, "Published");
      }
      setTrackerVersion((v) => v + 1);

      const action =
        mergedSuggestedActions.find((a) => a.id === actionId) ??
        safeActionLog.find((a) => a.id === actionId);
      const total = action?.artifacts?.length ?? 0;
      const generatedCount = artifactTracker.getForAction(actionId).length;
      const closedCount = artifactTracker.countClosed(actionId);

      if (total > 0 && closedCount >= total) {
        setCompletedActionIds((prev) => new Set([...Array.from(prev), actionId]));
      }

      const pubSugAction = safeFixActions.find((a) => a.id === actionId);
      const pubIngestedMatch = pubSugAction
        ? allActionsForLog.find(
            (l) =>
              l.title.trim().toLowerCase() === pubSugAction.title.trim().toLowerCase()
          )
        : undefined;

      pushEventStatus(
        effectiveBrandId,
        actionId,
        total,
        generatedCount,
        closedCount,
        pubIngestedMatch?.id
      );
    };

    window.addEventListener("artifact_published", handler);
    return () => window.removeEventListener("artifact_published", handler);
  }, [
    mergedSuggestedActions,
    safeFixActions,
    safeActionLog,
    allActionsForLog,
    effectiveBrandId,
  ]);

  const handleExplainWithAI = (topic: string) => {
    setAiAssistantTopic(topic);
    if (isSidebarCollapsed && onSidebarToggle) onSidebarToggle(false);
  };

  const handleStartAction = (actionId: number) => {
    setReturnToTab(activeTab);
    setGeneratingActionId(actionId);
    setGenerationTrayMode("setup");
    setGeneratingArtifact(null);
    setShowGenerationTray(true);
  };

  const handleGenerateAsset = (
    actionId: number,
    actionTitle: string,
    actionTheme: string,
    artifactTitle: string,
    artifactType: string,
    artifactDescription: string,
    artifactIndex: number = 0
  ) => {
    const action = mergedSuggestedActions.find((a) => a.id === actionId);
    const totalArtifacts = action?.artifacts?.length ?? 0;

    setReturnToTab(activeTab);
    setGeneratingActionId(actionId);
    setGenerationTrayMode("setup");
    setGeneratingArtifact(null);
    setShowGenerationTray(true);
    setGenerationCtx({
      actionTitle,
      actionTheme,
      artifactTitle,
      artifactType,
      artifactDescription,
      artifactIndex,
      actionId,
      totalArtifacts,
    });
  };

  const handleGenerate = async (config: GenerationConfig) => {
    if (!generatingActionId) return;
    setGenerationTrayMode("generating");
    try {
      const ctx = getGenerationCtx();

      const enrichedConfig = {
        ...config,
        selectedModels,
        slotIndex: ctx.artifactIndex ?? 0,
        slotTitle: ctx.artifactTitle || "",
      };

      const artifact = await fixActionsService.generateAsset({
        actionId: generatingActionId,
        actionTitle: ctx.actionTitle || "",
        actionTheme: ctx.actionTheme || "",
        artifactTitle: ctx.artifactTitle || "Optimization Asset",
        artifactType: ctx.artifactType || "Comparison Asset",
        artifactDescription: ctx.artifactDescription || "",
        config: enrichedConfig,
        brandId: effectiveBrandId,
        brandName: brandName || undefined,
      });

      setGenerationCtx({ artifactId: artifact.id });
      setGeneratingArtifact(artifact);
      setGenerationTrayMode("review");

      const freshCtx = getGenerationCtx();

      artifactTracker.track({
        artifactId: artifact.id,
        actionId: generatingActionId,
        artifactIndex: freshCtx.artifactIndex ?? 0,
        artifactTitle: artifact.name,
        artifactType: String(artifact.type),
        status: artifact.status || "Draft",
        generatedAt: artifact.generatedAt || new Date().toISOString(),
      });
      setTrackerVersion((v) => v + 1);

      setOptimisticStudioArtifacts((prev) => {
        const exists = prev.some((a) => a.id === artifact.id);
        return exists ? prev : [artifact, ...prev];
      });
      window.dispatchEvent(new Event("optimization_studio_refresh"));

      const total = freshCtx.totalArtifacts ?? 0;
      const generatedCount = artifactTracker.getForAction(generatingActionId).length;
      const closedCount = artifactTracker.countClosed(generatingActionId);

      const sugAction = safeFixActions.find((a) => a.id === generatingActionId);
      const ingestedMatch = sugAction
        ? allActionsForLog.find(
            (l) =>
              l.title.trim().toLowerCase() === sugAction.title.trim().toLowerCase()
          )
        : undefined;

      await pushEventStatus(
        effectiveBrandId,
        generatingActionId,
        total,
        generatedCount,
        closedCount,
        ingestedMatch?.id
      );
    } catch (e) {
      console.error("generate_asset failed", e);
      setGenerationTrayMode("setup");
    }
  };

  const handleApprove = async () => {
    const ctx = getGenerationCtx();
    const artifactId = ctx.artifactId || generatingArtifact?.id;
    if (artifactId) {
      try {
        await fixActionsService.approveGeneratedAsset({
          artifactId,
          brandId: effectiveBrandId,
          brandName: brandName || undefined,
        });
        setOptimisticStudioArtifacts((prev) =>
          prev.map((a) => (a.id === artifactId ? { ...a, status: "In Review" } : a))
        );
        artifactTracker.updateStatus(artifactId, "In Review");
        setTrackerVersion((v) => v + 1);
        window.dispatchEvent(new Event("deploy_log_refresh"));
      } catch (e) {
        console.error("approve_asset failed", e);
      }
    }
    setShowGenerationTray(false);
    setGeneratingActionId(null);
    setGeneratingArtifact(null);
    clearGenerationCtx();

    setActiveTab("insights");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("switch_to_deploy_tab"));
    }, 100);
  };

  const handleRegenerate = async () => {
    const ctx = getGenerationCtx();
    const artifactId = ctx.artifactId || generatingArtifact?.id;
    if (!artifactId) {
      setGenerationTrayMode("setup");
      return;
    }

    setGenerationTrayMode("generating");
    try {
      const updated = await fixActionsService.regenerateGeneratedAsset({
        artifactId,
        brandId: effectiveBrandId,
      });
      setGeneratingArtifact(updated);
      setOptimisticStudioArtifacts((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );
      artifactTracker.updateStatus(artifactId, updated.status || "Draft");
      setTrackerVersion((v) => v + 1);
    } catch (e) {
      console.error("regenerate_asset failed", e);
    } finally {
      setGenerationTrayMode("review");
    }
  };

  const closeTray = () => {
    setShowGenerationTray(false);
    setGeneratingActionId(null);
    setGeneratingArtifact(null);
    clearGenerationCtx();
    setActiveTab(returnToTab);
  };

  const handleNavigateToAction = (actionTitle: string) => {
    setHighlightActionTitle(actionTitle);
    setActiveTab("actions");
  };

  const handleNavigateToStudio = (artifactId: string) => {
    setHighlightStudioArtifactId(artifactId);
    setActiveTab("content");
  };

  const handleOpenArtifact = useCallback((artifact: StrategyArtifact) => {
    setOpenArtifact(artifact);
    setOptimisticStudioArtifacts((prev) => {
      if (prev.some((a) => a.id === artifact.id)) return prev;
      return [artifact, ...prev];
    });
  }, []);

  const handleArtifactRegenerate = useCallback(
    async (artifactId: string): Promise<StrategyArtifact | null> => {
      try {
        const updated = await fixActionsService.regenerateGeneratedAsset({
          artifactId,
          brandId: effectiveBrandId,
        });
        setOptimisticStudioArtifacts((prev) =>
          prev.map((a) => (a.id === updated.id ? updated : a))
        );
        setOpenArtifact(updated);
        window.dispatchEvent(new Event("optimization_studio_refresh"));
        return updated;
      } catch (e) {
        console.error("regenerate from tray failed", e);
        return null;
      }
    },
    [effectiveBrandId]
  );

  const handleArtifactStatusChange = useCallback(
    (artifactId: string, status: DeployStatus) => {
      // #133: Published is set by a publication record, not by this click. The
      // artifact goes to Brand Publisher as a draft; publishing it there (or
      // recording the publication by hand) marks it Published.
      if (status === "Published") {
        fixActionsService
          .sendArtifactToPublisher({ artifactId, brandId: effectiveBrandId, brandName })
          .then((r) => toast.success(
            `Sent to Brand Publisher as draft #${r.publishing_draft_id}. It reads Published once it is published from there.`))
          .catch((err) => toast.error(err?.message || "Could not send it to Brand Publisher"));
        return;
      }
      setOptimisticStudioArtifacts((prev) =>
        prev.map((a) => (a.id === artifactId ? { ...a, status } : a))
      );
      artifactTracker.updateStatus(artifactId, status);
      setTrackerVersion((v) => v + 1);
      window.dispatchEvent(new Event("optimization_studio_refresh"));

      fixActionsService
        .updateArtifactStatus({
          artifactId,
          status,
          brandId: effectiveBrandId,
          brandName: brandName || undefined,
        })
        .catch((err) => console.error("updateArtifactStatus failed", err));
    },
    [effectiveBrandId, brandName]
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value !== "actions") setIsBriefingMinimized(true);
  };

  const contentMarginClass = isSidebarCollapsed ? "lg:mr-12" : "lg:mr-[380px]";

  if ((loading || isFinishing) && !hasFinished) {
    return <FixActionsLoading brandName={brandName} isDone={isFinishing} />;
  }

  if (error) {
    return (
      <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <div
          className={`flex-1 overflow-auto flex items-center justify-center min-h-screen transition-all duration-300 ${contentMarginClass}`}
        >
          <div className="flex flex-col items-center gap-4 text-center max-w-md px-4">
            <AlertTriangle
              className="w-12 h-12"
              style={{ color: "var(--infrared-coral)" }}
            />
            <h3
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Failed to load data
            </h3>
            <p
              style={{
                fontSize: "var(--text-base)",
                color: "var(--muted-foreground)",
              }}
            >
              {(error as any)?.message || "Unable to load fix actions data."}
            </p>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar
            phase="fix"
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={onSidebarToggle}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
      <div className={`flex-1 overflow-auto transition-all duration-300 ${contentMarginClass}`}>
        {ACTION_ITEMS_UI ? (
          <ActionItemsShell
            brandName={brandName}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            highPriorityCount={highPriorityCount}
            quickWinsCount={quickWinsCount}
            completedCount={completedCount}
            actions={filteredActions}
            filterCategory={filterCategory}
            onFilterChange={setFilterCategory}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onGenerateAsset={handleGenerateAsset}
            onExplainWithAI={handleExplainWithAI}
            onNavigateToStudio={handleNavigateToStudio}
            highlightActionTitle={highlightActionTitle}
            onOpenArtifactTray={handleOpenArtifact}
            optimisticArtifacts={optimisticStudioArtifacts}
            brandId={effectiveBrandId}
            highlightStudioArtifactId={highlightStudioArtifactId}
            onClearStudioHighlight={() => setHighlightStudioArtifactId(null)}
            logSlot={
              <ActionLogView
                entries={allActionsForLog}
                allActions={[...safeActionLog, ...safeFixActions]}
                loading={false}
                error={null}
                brandId={effectiveBrandId}
                onNavigateToAction={handleNavigateToAction}
                onNavigateToAsset={(assetId) => {
                  setHighlightStudioArtifactId(assetId);
                  setActiveTab("content");
                }}
                completedActionIds={completedActionIds}
                initialExpandTitle={activeTab === "insights" ? highlightActionTitle : null}
                onInitialExpandConsumed={() => setHighlightActionTitle(null)}
                onArtifactStatusChange={handleArtifactStatusChange}
              />
            }
          />
        ) : (
        <div className="w-full px-4 md:px-8">
          <FixActionsHeader
            onNavigate={onNavigate}
            highPriorityCount={highPriorityCount}
            quickWinsCount={quickWinsCount}
            completedCount={completedCount}
            priorityUpdatedAt={null}
          />

          {quickWinAction && (
            <QuickWinBriefing
              quickWinAction={quickWinAction}
              isBriefingMinimized={isBriefingMinimized}
              setIsBriefingMinimized={setIsBriefingMinimized}
              onStartAction={handleStartAction}
              onGenerateAsset={handleGenerateAsset}
            />
          )}

          <ModuleTabNavigation
            primaryTabs={[
              { value: "actions", label: "Suggested Actions" },
              { value: "content", label: "Optimization Studio" },
              { value: "insights", label: "Action Log" },
            ]}
            activePrimaryTab={activeTab}
            onPrimaryTabChange={handleTabChange}
            secondaryFilters={
              activeTab === "actions"
                ? [
                    { value: "all", label: "All" },
                    { value: "high-impact", label: "High Impact" },
                    { value: "quick-wins", label: "Quick Wins" },
                    { value: "foundational", label: "Foundational" },
                  ]
                : undefined
            }
            activeSecondaryFilter={filterCategory}
            onSecondaryFilterChange={setFilterCategory}
            secondaryRightContent={
              activeTab === "actions" ? (
                <div className="relative w-full sm:w-auto">
                  <Search
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: "var(--muted-foreground)" }}
                  />
                  <input
                    type="text"
                    placeholder="Search actions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-60 pl-9 pr-3 py-2 border rounded-md"
                    style={{
                      backgroundColor: "var(--background)",
                      borderColor: "var(--border)",
                      fontSize: "var(--text-sm)",
                    }}
                  />
                </div>
              ) : undefined
            }
          >
            <TabPanel value="actions" >
              <SuggestedActionsSmartInbox
                actions={filteredActions}
                onExplainWithAI={handleExplainWithAI}
                onGenerateAsset={handleGenerateAsset}
                onNavigateToStudio={handleNavigateToStudio}
                isSidebarCollapsed={isSidebarCollapsed}
                highlightActionTitle={highlightActionTitle}
                trackerVersion={trackerVersion}
                brandId={effectiveBrandId}
              />
            </TabPanel>

            <TabPanel value="content" >
              <OptimizationStudioTab
                onOpenArtifactTray={handleOpenArtifact}
                onExplainWithAI={handleExplainWithAI}
                optimisticArtifacts={optimisticStudioArtifacts}
                brandId={effectiveBrandId}
                highlightArtifactId={highlightStudioArtifactId}
                onClearHighlight={() => setHighlightStudioArtifactId(null)}
              />
            </TabPanel>

            <TabPanel value="insights" >
              <ActionLogTab
                entries={allActionsForLog}
                allActions={[...safeActionLog, ...safeFixActions]}
                loading={false}
                error={null}
                brandId={effectiveBrandId}
                onNavigateToAction={handleNavigateToAction}
                onNavigateToAsset={(assetId) => {
                  setHighlightStudioArtifactId(assetId);
                  setActiveTab("content");
                }}
                completedActionIds={completedActionIds}
                initialExpandTitle={
                  activeTab === "insights" ? highlightActionTitle : null
                }
                onInitialExpandConsumed={() => setHighlightActionTitle(null)}
                onArtifactStatusChange={handleArtifactStatusChange}
              />
            </TabPanel>
          </ModuleTabNavigation>
        </div>
        )}
      </div>

      {/* AI Assistant — hidden below lg (< 1024px). */}
      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="fix"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
          initialTopic={aiAssistantTopic}
        />
      </div>

      {isTemplateGalleryOpen && (
        <TemplateGalleryModal
          isOpen={isTemplateGalleryOpen}
          onClose={() => {
            setIsTemplateGalleryOpen(false);
            setSelectedTemplate(null);
          }}
          selectedCategory={selectedTemplateCategory}
          onCategoryChange={setSelectedTemplateCategory}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={setSelectedTemplate}
          contentTemplates={safeTemplates}
        />
      )}

      {openArtifact &&
        (ACTION_ITEMS_UI ? (
          <RedesignArtifactDetailTray
            artifact={openArtifact}
            brandId={effectiveBrandId}
            onClose={() => setOpenArtifact(null)}
            onRegenerate={handleArtifactRegenerate}
            onStatusChange={handleArtifactStatusChange}
            isAssistantCollapsed={isSidebarCollapsed}
          />
        ) : (
          <ArtifactDetailTray
            artifact={openArtifact}
            brandId={effectiveBrandId}
            onClose={() => setOpenArtifact(null)}
            onRegenerate={handleArtifactRegenerate}
            onStatusChange={handleArtifactStatusChange}
            isAssistantCollapsed={isSidebarCollapsed}
          />
        ))}

      {showGenerationTray &&
        generatingActionId &&
        (() => {
          const ctx = getGenerationCtx();
          const TrayComp = ACTION_ITEMS_UI ? RedesignAssetGenerationTray : AssetGenerationTray;
          return (
            <TrayComp
              mode={generationTrayMode}
              artifact={generatingArtifact}
              supportingActionTitle={ctx.actionTitle || ""}
              supportingActionTheme={ctx.actionTheme || ""}
              artifactType={ctx.artifactType || "Comparison Asset"}
              objective={ctx.artifactDescription || ""}
              outputTypeDescription={`${
                ctx.artifactTitle || "Asset"
              } — structured content designed to influence AI reasoning.`}
              isAssistantCollapsed={isSidebarCollapsed}
              onClose={closeTray}
              onGenerate={handleGenerate}
              onApprove={handleApprove}
              onRegenerate={handleRegenerate}
              initialConfig={{
                brand: brandName || "",
                category: "",
                competitors: [],
                deploymentTargets: {
                  ownedSite: true,
                  thirdPartyBlogs: true,
                  forums: false,
                },
                tone: "Neutral",
                riskSensitivity: "Standard",
              }}
              modelsUsed={selectedModels}
            />
          );
        })()}
    </div>
  );
}